import { createContext, ReactNode, useContext, useReducer } from 'react';
import { useController, useSuspense } from 'rest-hooks';
import { ScoreModel } from '../interfaces/scoreModel';
import { LOCAL_STORAGE_KEYS } from '../localStorageKeys';
import { ScoreModelResource } from '../resources/scoreModelResource';

type Action = { type: 'switchScoreModel'; id: number | undefined };
type Dispatch = (action: Action) => void;
type State = { id: number | undefined };

const ScoreModelContext = createContext<{ state: State; dispatch: Dispatch } | undefined>(
  undefined
);

function scoreModelReducer(state: State, action: Action) {
  switch (action.type) {
    case 'switchScoreModel': {
      localStorage.setItem(LOCAL_STORAGE_KEYS.scoreModelStorageKey, `${action.id}`);
      return { id: action.id };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}!`);
    }
  }
}

export const ScoreModelProvider = ({ children }: { children: ReactNode }) => {
  const localStorageId = localStorage.getItem(LOCAL_STORAGE_KEYS.scoreModelStorageKey) ?? '';
  const id = parseInt(localStorageId);
  const initState = { id: id ? id : undefined };
  const [state, dispatch] = useReducer(scoreModelReducer, initState);
  const value = { state, dispatch };
  return <ScoreModelContext.Provider value={value}>{children}</ScoreModelContext.Provider>;
};

export const useScoreModel = () => {
  const { fetch } = useController();
  const context = useContext(ScoreModelContext);
  const scoreModelId = context?.state.id;
  const scoreModelResource = useSuspense(
    ScoreModelResource.detail(),
    scoreModelId
      ? {
          scoreModelId: scoreModelId
        }
      : null
  );
  return {
    scoreModel: scoreModelResource,
    switchScoreModel: (scoreModelId: number | undefined) => {
      context?.dispatch({ type: 'switchScoreModel', id: scoreModelId });
    },
    createScoreModel: (newScoremodel: ScoreModel) =>
      fetch(ScoreModelResource.create(), newScoremodel),
    updateScoreModel: (name: string) =>
      fetch(
        ScoreModelResource.update(),
        { scoreModelId: scoreModelId },
        { ...scoreModelResource, name: name }
      ),
    deleteScoreModel: () => fetch(ScoreModelResource.delete(), { scoreModelId: scoreModelId }),
    refresh: () => fetch(ScoreModelResource.detail(), { scoreModelId: scoreModelId })
  };
};
